import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { UploadedFile } from './index';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface FileUploadFormProps {
  uploadedFiles: UploadedFile[];
  handleFileUpload: (index: number, file: File) => Promise<void>;
  addFileUpload: () => void;
  removeFileUpload: (index: number) => void;
  setError?: (error: string | null) => void;
  validateUploadStep?: (isValid: boolean) => void;
  removeUploadedDocument?: (index: number) => Promise<void>;
  setUploadedFiles: any;
}

const FileUploadForm: React.FC<FileUploadFormProps> = ({
  uploadedFiles,
  handleFileUpload,
  addFileUpload,
  removeFileUpload,
  setError,
  validateUploadStep,
  removeUploadedDocument,
  setUploadedFiles
}) => {
  const [dragActive, setDragActive] = useState<{ [key: number]: boolean }>({});

  const hasUploadedFile = uploadedFiles.some(
    (file) => file.uploaded || file.file
  );

  useEffect(() => {
    if (validateUploadStep) {
      validateUploadStep(hasUploadedFile);
    }
  }, [hasUploadedFile, validateUploadStep, setError]);

  const handleRemoveUploadedDocument = async (index: number) => {
    if (removeUploadedDocument) {
      await removeUploadedDocument(index);
    }
  };

  // Validate file extension and size
  const validateFile = (file: File): string | null => {
    const validExtensions = ['.pdf', '.xls', '.xlsx', '.doc', '.docx', '.zip'];
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    // Validate file type
    if (!validExtensions.includes(fileExtension)) {
      return `Invalid file type. Only ${validExtensions.join(
        ', '
      )} files are allowed.`;
    }

    // Validate file size (max 10MB)
    const maxSize = 200 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return `File is too large. Maximum file size is 200MB.`;
    }

    return null;
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive((prev) => ({ ...prev, [index]: true }));
    } else if (e.type === 'dragleave') {
      setDragActive((prev) => ({ ...prev, [index]: false }));
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive((prev) => ({ ...prev, [index]: false }));

    if (uploadedFiles[index].isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const selectedFile = files[0];
      const validationError = validateFile(selectedFile);

      if (validationError) {
        // // Update the file entry with the error
        const newFiles = [...uploadedFiles];
        newFiles[index] = {
          ...newFiles[index],
          file: null,
          title: 'Bid Document',
          uploaded: false
        };
        setUploadedFiles(newFiles);

        // // Set global error
        setError(validationError);
      } else {
        // Proceed with upload if validation passes
        setError(null);
        handleFileUpload(index, selectedFile);
      }
      // Reset the file input value to allow re-uploading the same file
    }
  };

  // Handle file selection
  const handleFileSelect = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const validationError = validateFile(file);

      if (validationError) {
        // Update the file entry with the error
        // const newFiles = [...uploadedFiles];
        // newFiles[index] = {
        //   ...newFiles[index],
        //   file: e.target.files?.[0],
        //   uploaded: false,
        //   uploadError: validationError
        // };
        // setUploadedFiles(newFiles);

        // Set global error if needed
        const newFiles = [...uploadedFiles];
        newFiles[index] = {
          ...newFiles[index],
          file: null,
          title: 'Bid Document',
          uploaded: false
        };
        setUploadedFiles(newFiles);
        setError(validationError);
      } else {
        // Proceed with upload if validation passes
        handleFileUpload(index, e.target.files?.[0]);
        setError(null);
      }

      // Reset the file input value to allow re-uploading the same file
      e.target.value = '';
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        You can only upload files with extension -
        .pdf/.xls/.xlsx/.doc/.docx/.zip
      </Alert>

      {!hasUploadedFile && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          At least one document must be uploaded before proceeding
        </Alert>
      )}

      {uploadedFiles.map((file, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            {file.isUploading ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2">Uploading...</Typography>
              </Box>
            ) : (
              <>
                {/* Drag & Drop Zone */}
                <Paper
                  onDragEnter={(e) => handleDrag(e, index)}
                  onDragLeave={(e) => handleDrag(e, index)}
                  onDragOver={(e) => handleDrag(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  sx={{
                    p: 2,
                    border: dragActive[index]
                      ? '2px dashed #1976d2'
                      : '2px dashed #ccc',
                    backgroundColor: dragActive[index]
                      ? 'rgba(25, 118, 210, 0.08)'
                      : 'transparent',
                    borderRadius: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <input
                    type="file"
                    hidden
                    id={`file-input-${index}`}
                    accept=".pdf,.xls,.xlsx,.doc,.docx,.zip"
                    onChange={(e) => handleFileSelect(index, e)}
                  />

                  <label
                    htmlFor={`file-input-${index}`}
                    style={{ cursor: 'pointer', display: 'block' }}
                  >
                    <CloudUploadIcon
                      sx={{
                        fontSize: 40,
                        color: file.uploadError
                          ? 'error.main'
                          : file.file
                          ? 'success.main'
                          : 'primary.main',
                        mb: 1
                      }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {file.uploaded
                        ? 'Drop file to replace or click'
                        : 'Drag & drop file here or click to browse'}
                    </Typography>
                  </label>

                  {file.file && (
                    <Typography
                      variant="caption"
                      display="block"
                      color={file.uploadError ? 'error' : 'textSecondary'}
                      sx={{
                        mt: 1,
                        fontWeight: 'medium',
                        wordWrap: 'break-word',
                        whiteSpace: 'normal',
                        maxWidth: '100%'
                      }}
                    >
                      📄 {file.file.name}
                      {file.uploadError && ` - ${file.uploadError}`}
                    </Typography>
                  )}

                  {file.fileUrl && !file.file && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 1
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        color={file.uploaded ? 'success.main' : 'textSecondary'}
                        sx={{ fontWeight: 'medium' }}
                      >
                        ✅{' '}
                        {file.uploaded
                          ? 'Successfully uploaded: '
                          : 'Existing file: '}
                        {file.fileName || file.title}
                      </Typography>

                      {file.uploaded && removeUploadedDocument && (
                        <Tooltip title="Remove uploaded document">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveUploadedDocument(index)}
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Paper>
              </>
            )}
          </Grid>

          <Grid item xs={12} sm={2}>
            {index > 0 && !file.isUploading && (
              <Button
                color="error"
                onClick={() => removeFileUpload(index)}
                fullWidth
              >
                Remove
              </Button>
            )}
          </Grid>

          {file.uploadError && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mt: 1 }}>
                {file.uploadError}
              </Alert>
            </Grid>
          )}

          {file.uploaded && !file.uploadError && (
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mt: 1 }}>
                File uploaded successfully
              </Alert>
            </Grid>
          )}

          {!file.file && !file.uploaded && !file.isUploading && index === 0 && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                Please upload the required document
              </Alert>
            </Grid>
          )}
        </Grid>
      ))}

      <Button
        variant="outlined"
        onClick={addFileUpload}
        sx={{ mt: 2 }}
        disabled={uploadedFiles.length > 0 && !hasUploadedFile}
      >
        Add Another File
      </Button>

      <Typography
        variant="body2"
        sx={{ mt: 4, color: 'text.secondary', fontWeight: 'bold' }}
      >
        Note: ⏳ Uploading large files may take some time to complete.
      </Typography>
    </Box>
  );
};

export default FileUploadForm;
